import { useState } from "react";
import { useI18n } from "../lib/i18n.jsx";
import { auth, db } from "../lib/firebase.js";
import { signIn, subscribeMarketplaceTeams } from "../lib/api.js";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { useEffect } from "react";

export default function MarketplaceHome({ user }) {
  const { t, lang } = useI18n();
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [showLogin, setShowLogin] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("login") === "1") setShowLogin(true);
  }, []);

  useEffect(() => {
    if (!showLogin) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("login") === "1") {
      const url = new URL(window.location);
      url.searchParams.delete("login");
      window.history.replaceState(null, "", url.pathname + url.search);
    }
  }, [showLogin]);

  useEffect(() => {
    const unsub = subscribeMarketplaceTeams((data) => setTeams(data));
    return unsub;
  }, []);

  const categories = [...new Set(teams.map((t) => t.category).filter(Boolean))];

  const filtered = teams
    .filter((t) => {
      if (category !== "all" && t.category !== category) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return t.name?.toLowerCase().includes(q) ||
        t.tags?.some((tag) => tag.toLowerCase().includes(q)) ||
        t.category?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sort === "newest") return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      if (sort === "top_rated") return (b.avgRating || 0) - (a.avgRating || 0);
      return (b.installCount || 0) - (a.installCount || 0);
    });

  function navigateTo(path) {
    window.history.pushState(null, "", path);
    window.dispatchEvent(new Event("popstate"));
  }

  async function handleSignIn() {
    setSigningIn(true);
    try {
      await signIn();
      setShowLogin(false);
    } catch (e) {
      console.error("Sign in error:", e);
    }
    setSigningIn(false);
  }

  return (
    <div className="marketplace-home">
      <div className="marketplace-hero">
        <h1>{t("title")}</h1>
        <p className="hero-subtitle">{t("subtitle")}</p>
        <div className="search-bar">
          <input
            type="text"
            placeholder={t("search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
      </div>

      <div className="marketplace-filters">
        <div className="filter-group">
          <label>{t("categories")}</label>
          <div className="filter-chips">
            <button className={`chip ${category === "all" ? "active" : ""}`} onClick={() => setCategory("all")}>
              {t("all")}
            </button>
            {categories.map((c) => (
              <button key={c} className={`chip ${category === c ? "active" : ""}`} onClick={() => setCategory(c)}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group sort-group">
          <label>{t("sort_by")}</label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="sort-select">
            <option value="popular">{t("popular")}</option>
            <option value="newest">{t("newest")}</option>
            <option value="top_rated">{t("top_rated")}</option>
          </select>
        </div>
      </div>

      <div className="teams-grid">
        {filtered.map((team) => (
          <a key={team.id} href={`/team/${team.slug}`}
             className="team-card"
             onClick={(e) => { e.preventDefault(); navigateTo(`/team/${team.slug}`); }}>
            <div className="team-card-header">
              <h3>{team.name}</h3>
              <span className={`price-badge ${team.price ? "paid" : "free"}`}>
                {team.price ? `${team.price}€` : t("free")}
              </span>
            </div>
            <p className="team-card-desc">
              {team.short_desc_en || team.short_desc_es || ""}
            </p>
            <div className="team-card-meta">
              <span className="meta-rating">★ {team.avgRating?.toFixed(1) || "—"}</span>
              <span className="meta-reviews">{t("review_count", { n: team.reviewCount || 0 })}</span>
              <span className="meta-installs">{t("install_count", { n: team.installCount || 0 })}</span>
            </div>
            {team.tags?.length > 0 && (
              <div className="team-card-tags">
                {team.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="mini-tag">{tag}</span>
                ))}
              </div>
            )}
          </a>
        ))}
        {filtered.length === 0 && (
          <div className="empty-state">
            <p>{t("no_results")}</p>
          </div>
        )}
      </div>

      {showLogin && !user && (
        <div className="login-overlay">
          <div className="login-modal">
            <h2>{t("sign_in_required")}</h2>
            <p>{t("sign_in")}</p>
            <button className="btn btn-primary btn-google" onClick={handleSignIn} disabled={signingIn}>
              <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {signingIn ? t("signing_in") : t("login_with_google")}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowLogin(false)}>{t("cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
